import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import {
  useApplicationsReportQuery,
  useAverageDataQuery,
  useCountriesReportQuery,
  useDiffrentReportQuery,
  useReportChartQuery,
} from "api/report/report.mutation";
import { countries } from "constants/countries";
import jalaali from "jalaali-js";
import { ResponsiveBar } from "nivo/lib/components/charts/bar";
import { useMemo } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import DiffrentChart from "./diffrentChart/DiffrentChart";
import PieChart from "./pieChart/PieChart";
import classes from "./style.module.scss";

const dateConverter = (gregorianDate) => {
  const dateParts = gregorianDate.split("-").map((part) => parseInt(part, 10));
  const dateObject = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);

  const shamsiDate = jalaali.toJalaali(
    dateObject.getFullYear(),
    dateObject.getMonth() + 1,
    dateObject.getDate()
  );
  return `${shamsiDate.jy}-${shamsiDate.jm}-${shamsiDate.jd}`;
};

const Report = () => {
  const countriesReportQuery = useCountriesReportQuery();
  const applicationsReportQuery = useApplicationsReportQuery();
  const diffrentsReportQuery = useDiffrentReportQuery();
  const reportChartQuery = useReportChartQuery();
  const averageDataQuery = useAverageDataQuery();
  const countriesReport = countriesReportQuery.data;
  const diffrentsReport = diffrentsReportQuery.data;
  const averageData = averageDataQuery.data;
  const applicationsReport = useMemo(() => {
    const response = applicationsReportQuery.data;
    if (!response) return [];

    return [
      {
        id: "قبول شده",
        label: "قبول شده",
        value: response?.report?.acpt_count,
      },
      {
        id: "رد شده",
        label: "رد شده",
        value: response?.report?.rjct_count,
      },
      {
        id: "در دست بررسی",
        label: "در دست بررسی",
        value: response?.report?.crnt_count,
      },
      {
        id: "نقص مدارک",
        label: "نقص مدارک",
        value: response?.report?.ntet_count,
      },
    ];
  }, [applicationsReportQuery.data]);
  const barChartData = useMemo(
    () =>
      (reportChartQuery.data ?? []).map((item) => ({
        date: dateConverter(item.date),
        کارشناسی: item.bachelor,
        "کارشناسی ارشد": item.master,
        دکترا: item.phd,
      })),
    [reportChartQuery.data]
  );

  return (
    <>
      <div className={classes.mainDiv}>
        <Grid container spacing={2} className={classes.force_grid} direction="row-reverse">
          <Grid item xs={12} md={6} lg={3}>
            <div className={classes.box} style={{ padding: "20px 0" }}>
              <div className={classes.boxHeader}>
                <Typography className={classes.boxHeaderTitle}>پراکندگی درخواست ها</Typography>
                <div className={classes.boxHeaderTimes}>
                  <Typography fontSize={12} className={classes.boxHeaderTimeItem}>
                    کل
                  </Typography>
                  <Typography fontSize={12} className={classes.boxHeaderTimeItem}>
                    سال
                  </Typography>
                  <Typography fontSize={12} className={classes.boxHeaderTimeItem}>
                    ماه
                  </Typography>
                </div>
              </div>
              <div
                className={classes.countriesRequests}
                style={{ maxHeight: "280px", minHeight: "280px", overflow: "auto" }}
              >
                {countriesReport?.countries?.map((item, index) => {
                  let country = countries.find((node) => node.value === item.country);
                  return (
                    <div
                      className={`${classes.countriesRequestsItem} ${
                        index / 2 === 0 && classes.rowDark
                      }`}
                      key={index}
                    >
                      <div className={classes.countriesRequestsItemBox}>
                        <Typography className={classes.countriesRequestsItemTitle}>
                          {country?.label}
                        </Typography>

                        <LazyLoadImage
                          src={`https://www.worldometers.info/img/flags/${country?.code.toLowerCase()}-flag.gif`}
                          className={classes.countriesRequestsItemFlag}
                        />
                      </div>
                      <div className={classes.countriesRequestsItemBox}>
                        <Typography className={classes.countriesRequestsItemCount}>
                          {Number(item?.total_applications).toLocaleString("fa-ir")}
                        </Typography>
                        <Typography className={classes.countriesRequestsItemPercent}>
                          (
                          {Number(
                            (item?.total_applications / countriesReport?.application_count) * 100
                          ).toLocaleString("fa-ir")}
                          %)
                        </Typography>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <div className={classes.box} style={{ padding: "32px 0" }}>
              <div className={classes.boxHeader}>
                <Typography className={classes.boxHeaderTitle}>وضعیت درخواست ها</Typography>
                <div className={classes.boxHeaderTimes}>
                  <Typography fontSize={12} className={classes.boxHeaderTimeItem}>
                    کل
                  </Typography>
                  <Typography fontSize={12} className={classes.boxHeaderTimeItem}>
                    سال
                  </Typography>
                  <Typography fontSize={12} className={classes.boxHeaderTimeItem}>
                    ماه
                  </Typography>
                </div>
              </div>
              <div className={classes.chartNivoPieBox}>
                <PieChart data={applicationsReport} />
              </div>
            </div>
          </Grid>
          <Grid item xs={12} md={6} lg={5}>
            <div className={classes.verticalContainer}>
              <div className={classes.box} style={{ padding: "16px 0" }}>
                <div className={classes.boxHeader}>
                  <Typography className={classes.boxHeaderTitle}>جنسیت</Typography>
                  <div className={classes.boxHeaderTimes}>
                    <Typography fontSize={12} className={classes.boxHeaderTimeItem}>
                      کل
                    </Typography>
                    <Typography fontSize={12} className={classes.boxHeaderTimeItem}>
                      سال
                    </Typography>
                    <Typography fontSize={12} className={classes.boxHeaderTimeItem}>
                      ماه
                    </Typography>
                  </div>
                </div>
                <div className={classes.diffrentBody}>
                  {diffrentsReport?.gender?.male > 0 && (
                    <div className={classes.diffrentBodyItem}>
                      <Typography fontSize={14} className={classes.firstColor}>
                        آقا:
                      </Typography>
                      <div className={classes.diffrentBodyItemValue}>
                        <Typography fontSize={14} className={classes.countriesRequestsItemPercent}>
                          (
                          {Number(
                            (diffrentsReport?.gender?.male / diffrentsReport?.application_count) *
                              100
                          ).toLocaleString("fa-ir")}
                          %)
                        </Typography>
                        <Typography fontSize={14} className={classes.countriesRequestsItemCount}>
                          {Number(diffrentsReport?.gender?.male).toLocaleString("fa-ir")}
                        </Typography>
                      </div>
                    </div>
                  )}
                  {diffrentsReport?.gender?.female > 0 && (
                    <div className={classes.diffrentBodyItem}>
                      <Typography fontSize={14} className={classes.secondColor}>
                        خانم:
                      </Typography>
                      <div className={classes.diffrentBodyItemValue}>
                        <Typography fontSize={14} className={classes.countriesRequestsItemPercent}>
                          (
                          {Number(
                            (diffrentsReport?.gender?.female / diffrentsReport?.application_count) *
                              100
                          ).toLocaleString("fa-ir")}
                          %)
                        </Typography>
                        <Typography fontSize={14} className={classes.countriesRequestsItemCount}>
                          {Number(diffrentsReport?.gender?.female).toLocaleString("fa-ir")}
                        </Typography>
                      </div>
                    </div>
                  )}
                </div>
                <div className={classes.chartNivoDiffrentBox}>
                  <DiffrentChart data={[diffrentsReport?.gender]} keys={["male", "female"]} />
                </div>
              </div>
              <div className={classes.box} style={{ padding: "16px 0" }}>
                <div className={classes.boxHeader}>
                  <Typography className={classes.boxHeaderTitle}>مقطع</Typography>
                  <div className={classes.boxHeaderTimes}>
                    <Typography fontSize={12} className={classes.boxHeaderTimeItem}>
                      کل
                    </Typography>
                    <Typography fontSize={12} className={classes.boxHeaderTimeItem}>
                      سال
                    </Typography>
                    <Typography fontSize={12} className={classes.boxHeaderTimeItem}>
                      ماه
                    </Typography>
                  </div>
                </div>
                <div className={classes.diffrentBody}>
                  {diffrentsReport?.education?.bachelor > 0 && (
                    <div className={classes.diffrentBodyItem}>
                      <Typography fontSize={14} className={classes.firstColor}>
                        کارشناسی:
                      </Typography>
                      <div className={classes.diffrentBodyItemValue}>
                        <Typography fontSize={14} className={classes.countriesRequestsItemPercent}>
                          (
                          {Number(
                            (diffrentsReport?.education?.bachelor /
                              diffrentsReport?.application_count) *
                              100
                          ).toLocaleString("fa-ir")}
                          %)
                        </Typography>
                        <Typography fontSize={14} className={classes.countriesRequestsItemCount}>
                          {Number(diffrentsReport?.education?.bachelor).toLocaleString("fa-ir")}
                        </Typography>
                      </div>
                    </div>
                  )}
                  {diffrentsReport?.education?.master > 0 && (
                    <div className={classes.diffrentBodyItem}>
                      <Typography fontSize={14} className={classes.secondColor}>
                        ارشد:
                      </Typography>
                      <div className={classes.diffrentBodyItemValue}>
                        <Typography fontSize={14} className={classes.countriesRequestsItemPercent}>
                          (
                          {Number(
                            (diffrentsReport?.education?.master /
                              diffrentsReport?.application_count) *
                              100
                          ).toLocaleString("fa-ir")}
                          %)
                        </Typography>
                        <Typography fontSize={14} className={classes.countriesRequestsItemCount}>
                          {Number(diffrentsReport?.education?.master).toLocaleString("fa-ir")}
                        </Typography>
                      </div>
                    </div>
                  )}
                  {diffrentsReport?.education?.phd > 0 && (
                    <div className={classes.diffrentBodyItem}>
                      <Typography fontSize={14} className={classes.thirdColor}>
                        دکتری:
                      </Typography>
                      <div className={classes.diffrentBodyItemValue}>
                        <Typography fontSize={14} className={classes.countriesRequestsItemPercent}>
                          (
                          {Number(
                            (diffrentsReport?.education?.phd / diffrentsReport?.application_count) *
                              100
                          ).toLocaleString("fa-ir")}
                          %)
                        </Typography>
                        <Typography fontSize={14} className={classes.countriesRequestsItemCount}>
                          {Number(diffrentsReport?.education?.phd).toLocaleString("fa-ir")}
                        </Typography>
                      </div>
                    </div>
                  )}
                </div>
                <div className={classes.chartNivoDiffrentBox}>
                  <DiffrentChart
                    data={[diffrentsReport?.education]}
                    keys={["bachelor", "master", "phd"]}
                  />
                </div>
              </div>
            </div>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <div className={classes.box} style={{ padding: "20px 0" }}>
              <div className={classes.boxHeader}>
                <Typography className={classes.boxHeaderTitle}>میانگین زمان بررسی</Typography>
              </div>
              <div
                className={classes.countriesRequests}
                style={{ maxHeight: "280px", minHeight: "280px", overflow: "auto" }}
              >
                {averageData?.map((item, index) => (
                  <div className={classes.countriesRequestsItem} key={index}>
                    <div className={classes.countriesRequestsItemBox}>
                      <Typography className={classes.countriesRequestsItemTitle}>
                        {item?.field_of_study}
                      </Typography>
                    </div>
                    <div className={classes.countriesRequestsItemBox}>
                      <Typography
                        className={classes.countriesRequestsItemCount}
                        style={{ direction: "rtl" }}
                      >
                        <span style={{ direction: "rtl" }}>
                          {Number(item?.days).toLocaleString("fa-ir")} روز
                        </span>{" "}
                        <span style={{ direction: "rtl" }}>
                          {Number(item?.hours).toLocaleString("fa-ir")} ساعت
                        </span>
                      </Typography>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Grid>
          <Grid item xs={12} lg={9}>
            <div className={classes.box} style={{ paddingBottom: "20px" }}>
              <div className={classes.boxHeader}>
                <Typography className={classes.boxHeaderTitle} style={{ marginBottom: "-30px" }}>
                  آمار ها و درخواست ها
                </Typography>
              </div>
              <div className={classes.chart_field} style={{ height: "324px", width: "100%" }}>
                <ResponsiveBar
                  data={barChartData}
                  keys={["master", "bachelor", "phd"]}
                  indexBy="date"
                  margin={{ top: 50, right: 130, bottom: 50, left: 60 }}
                  padding={0.6}
                  groupMode="grouped"
                  valueScale={{ type: "linear" }}
                  indexScale={{ type: "band", round: true }}
                  colors={[
                    "var(--chart-responsive-bar-first-color)",
                    "var(--chart-responsive-bar-second-color)",
                    "var(--chart-responsive-bar-third-color)",
                  ]}
                  axisTop={null}
                  axisRight={null}
                  labelSkipWidth={12}
                  labelSkipHeight={12}
                  innerPadding={2}
                  borderRadius={4}
                  role="application"
                  ariaLabel="Nivo bar chart demo"
                  barAriaLabel={(e) => e.id + ": " + e.formattedValue + " in date: " + e.indexValue}
                  axisBottom={{
                    tickSize: 7,
                    tickPadding: 6,
                    tickRotation: -39,
                    legendPosition: "middle",
                    legendOffset: 32,
                    truncateTickAt: 90,
                  }}
                />
              </div>
            </div>
          </Grid>
        </Grid>
      </div>
    </>
  );
};

export default Report;
